import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { ROUTES } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Skeleton } from '@/components/ui/skeleton';
import { PlayCircle, BookOpen, CheckCircle, Award, Clock } from 'lucide-react';
import { useClientLogos } from '@/hooks/useClientLogos';
import Marketing from './Marketing';

const steps = [
  { icon: PlayCircle, text: "Watch short training videos" },
  { icon: BookOpen, text: "Review key concepts with flashcards" },
  { icon: CheckCircle, text: "Complete a quick assessment" },
  { icon: Award, text: "Download your Compliance Certificate" },
];

const getPageType = (): 'marketing' | 'welcome' => {
  // Allow override via query param for local testing
  const urlParams = new URLSearchParams(window.location.search);
  const pageOverride = urlParams.get('page');
  if (pageOverride === 'welcome') return 'welcome';
  if (pageOverride === 'marketing') return 'marketing';

  const hostname = window.location.hostname;
  
  // localhost → marketing (dev testing)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'marketing';
  }
  
  const parts = hostname.split('.');
  
  // www.ditancompliance.co.za → marketing (www is not a client)
  // ditancompliance.co.za → marketing (no subdomain)
  // konica.ditancompliance.co.za → welcome (client subdomain)
  
  const isClientSubdomain = parts.length > 2 && !parts[0].startsWith('www');
  
  return isClientSubdomain ? 'welcome' : 'marketing';
};

export default function Welcome() {
  const navigate = useNavigate();
  const { session, isLoading } = useAuth();
  const { data: client } = useClientLogos();
  
  // Subdomain detection with query param override for local testing
  const pageType = useMemo(() => getPageType(), []);

  useEffect(() => {
    // Only redirect authenticated users on the welcome page (client subdomain)
    if (session && !isLoading && pageType === 'welcome') {
      navigate(ROUTES.DASHBOARD);
    }
  }, [session, isLoading, navigate, pageType]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Render marketing page for main domain
  if (pageType === 'marketing') {
    return <Marketing />;
  }

  // Render welcome page for client subdomains
  return (
    <div className="flex min-h-screen flex-col bg-background safe-y">
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        {/* Section 1: Logos */}
        <div className="rounded-2xl px-8 py-6 border-2 border-primary shadow-lg shadow-primary/20 bg-gradient-to-br from-background/80 to-background/60 backdrop-blur-sm mb-12">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <div className="bg-[hsl(40,30%,95%)] rounded-full px-8 py-4 flex items-center justify-center min-w-[12rem] h-20">
              {client?.primaryLogoUrl ? (
                <img src={client.primaryLogoUrl} alt="Legal Partner" className="max-h-12 object-contain" loading="lazy" />
              ) : (
                <Skeleton className="h-10 w-32" />
              )}
            </div>
            
            <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-primary/20">
              <span className="text-primary text-xl font-light">+</span>
            </div>
            <div className="sm:hidden h-px w-16 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
            
            <div className="bg-[hsl(40,30%,95%)] rounded-full px-8 py-4 flex items-center justify-center min-w-[12rem] h-20">
              {client?.clientLogoUrl ? (
                <img src={client.clientLogoUrl} alt="Client" className="max-h-12 object-contain" loading="lazy" />
              ) : (
                <Skeleton className="h-10 w-32" />
              )}
            </div>
          </div>
        </div>

        {/* Section 2: Welcome */}
        <div className="text-center mb-12 max-w-xl">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
            Welcome to Your
          </h1>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent mb-6">
            Compliance Training Portal
          </h1>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild size="lg" className="w-full sm:w-auto touch-target shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow">
              <Link to={ROUTES.LOGIN}>Sign In</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto touch-target border-primary/30 hover:border-primary/50">
              <Link to={ROUTES.REGISTER}>Register</Link>
            </Button>
          </div>
        </div>

        {/* Section 3: Why This Matters */}
        <div className="text-center mb-12 max-w-lg">
          <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
            Why This Matters
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Understanding company policies protects both you and the company. Complete your 
            company policy training and get compliant in just a few simple steps.
          </p>
        </div>

        {/* Section 4: What You'll Do */}
        <div className="mb-12 w-full max-w-md">
          <h2 className="text-lg font-heading font-semibold text-foreground text-center mb-6">
            What You'll Do
          </h2>
          <div className="space-y-3">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div 
                  key={index}
                  className="group flex items-center gap-4 p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 hover:bg-card/80 transition-all duration-300"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 group-hover:border-primary/40 transition-colors">
                    <IconComponent className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-foreground font-medium">{step.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 5: Time Estimate - Premium Highlight */}
        <div className="mb-10 w-full max-w-md">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary/15 via-primary/10 to-transparent border border-primary/20 p-5 shadow-lg">
            <div className="absolute top-0 right-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl -mr-8 -mt-8" />
            <div className="relative flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/20 border border-primary/30">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-heading font-semibold text-primary mb-1">Quick & Easy</p>
                <p className="text-sm text-primary/90 leading-relaxed">
                  Just 30 minutes a day — most employees complete their training in a few days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 6: Footer */}
      <footer className="py-6 text-center">
        <p className="text-sm text-muted-foreground">
          © 2025 Chalwe &amp; Kabalata Attorneys. Questions? Contact your HR team.
        </p>
      </footer>
    </div>
  );
}
